import { OperationStatusEnum } from "@/lib/enum/OperationStatus.enum";
import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import { handleError } from "@/lib/utility/handleError";
import operationService from "@/services/mrfiktiv/services/operationService";
import { ThgPartnerOperationViewModelGen, ThgTimestampDocumentGen } from "@/services/thg/v1/data-contracts";
import { Timestamp } from "./timestamp.entity";
import { CronJobEnum } from "@/lib/enum/CronJob.enum";
import { IEntity } from "@/lib/utility/data/entity.interface";
import { MrfiktivOperationViewModelGen } from "@/services/mrfiktiv/v1/data-contracts";
import { IHasPartnerId } from "@/lib/interfaces/has-partner-id.interface.ts";
import { OperationDataAccessLayer } from "@/store/modules/access-layers/operation.access-layer";

@IsFilterable
class OperationBase implements MrfiktivOperationViewModelGen, Partial<IHasPartnerId>, Partial<IEntity> {
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.operation.partnerId"
  })
  partnerId?: string;

  @FilterConfig({
    type: FilterTypes.ENUM,
    config: { items: Object.values(OperationStatusEnum) },
    displayName: "objects.operation.status"
  })
  status: OperationStatusEnum;

  @FilterConfig({
    type: FilterTypes.ENUM,
    config: { items: Object.values(CronJobEnum) },
    displayName: "objects.operation.status"
  })
  type: CronJobEnum;

  @FilterConfig({
    type: FilterTypes.BOOLEAN,
    displayName: "objects.operation.isCanceled"
  })
  isCanceled: boolean;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.operation.message"
  })
  message: string;

  @FilterConfig({
    type: Timestamp
  })
  timestamp: ThgTimestampDocumentGen;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.operation.id"
  })
  _id: string;

  id: string;

  body?: object | undefined;

  error: object;

  constructor(operation?: Partial<ThgPartnerOperationViewModelGen>) {
    this.partnerId = operation?.partnerId;
    this.status = operation?.status as OperationStatusEnum;
    this.type = (operation?.type ?? CronJobEnum.DEFAULT) as CronJobEnum;
    this.isCanceled = !!operation?.isCanceled;
    this.message = operation?.message || "";
    this.timestamp = (operation?.timestamp ?? {}) as ThgTimestampDocumentGen;
    this._id = operation?.id ?? "";
    this.id = operation?.id ?? "";
    this.error = operation?.error || {};
    this.body = operation?.body || {};
  }

  private async map(operation: ThgPartnerOperationViewModelGen | MrfiktivOperationViewModelGen) {
    this.partnerId = (operation as ThgPartnerOperationViewModelGen).partnerId;
    this.status = operation.status as OperationStatusEnum;
    this.type = (operation?.type ?? CronJobEnum.DEFAULT) as CronJobEnum;
    this.isCanceled = !!operation.isCanceled;
    this.message = operation.message || "";
    this.timestamp = operation.timestamp as ThgTimestampDocumentGen;
    this._id = operation?.id ?? "";
    this.id = operation.id;
    this.error = operation.error || {};
    this.body = operation.body || {};
  }

  async fetch() {
    if (this.partnerId) {
      await operationService
        .getOperationByPartner(this.partnerId, this.id)
        .then(b => this.map(b))
        .catch(handleError);
    } else {
      await operationService
        .getOperation(this.id)
        .then(b => this.map(b))
        .catch(handleError);
    }

    OperationDataAccessLayer.set(this);

    return this;
  }

  async cancel() {
    if (this.partnerId) {
      await operationService
        .cancelOperationByPartnerId(this.id, this.partnerId)
        .then(b => this.map(b))
        .catch(handleError);
    } else {
      await operationService
        .cancelOperation(this.id)
        .then(b => this.map(b))
        .catch(handleError);
    }

    OperationDataAccessLayer.set(this);

    return this;
  }
}
type IOperation = OperationBase;
const Operation = Filter.createForClass(OperationBase);

export { IOperation, Operation };
